import { useMemo } from 'react';
import { autosApi } from '../plugins/data/api';
import { automationItem } from '../plugins/data/items';
import { useLoad } from '../plugins/data/hooks';
import { frequencyWord } from '../plugins/data/words';
import { usePluginNames, toolNameOf } from '../plugins/data/elsewhere';

// A SCHEDULE'S OR AN AUTOMATION'S CONTRACT (an `automation` grant): the access a
// tool's schedule, or an event or manual automation, runs with. The server mints it
// paused and never switches it on (runner.mint_standing_grant: a run reads it through
// its own context), so its `is_active` says nothing: whether it runs is the
// automation's state, read from the automations list, in the plugin words — a
// tool that runs automatically is On · Off · Can't run · Keeps failing, an automation is
// On · Off · Failing. Its realm is chosen with the schedule, so it is changed there.

export const isStanding = (p) => p?.entityType === 'automation';

/** The schedule's or automation's door: the tool's Schedule tab, or the automation. */
export function standingDoor(a, automationId, to) {
    if (a?.toolSchedule && a.pluginId) {
        return {
            label: 'Open the tool',
            href: to('plugins', null, `open=${encodeURIComponent(a.pluginId)}&tool=${encodeURIComponent(a.toolId || '')}&tab=schedule`),
        };
    }
    return { label: 'Open automation', href: to('plugins', 'automations', `open=${encodeURIComponent(a?.id || automationId)}`) };
}

/** What one automation grant stands for: { schedule, name, word, state, of, tone,
 *  why, when, known }. `a` is its automation (automationItem), or null when the
 *  list does not hold it (not read, or the automation is gone). */
export function standingOf(p, a, names) {
    if (!a) return { schedule: false, name: p.label, known: false, automation: null, of: 'automation', state: null, word: null, tone: null, why: null, when: null };
    const schedule = a.toolSchedule;
    const named = schedule && a.pluginId && names?.[String(a.pluginId)] ? `${toolNameOf(names, a.pluginId, a.toolId)} · runs automatically` : null;
    const { of, state, word, tone, why } = a.status;
    return {
        schedule,
        name: named || (schedule && a.name ? `${a.name} · runs automatically` : a.name || p.label),
        known: true,
        automation: a,
        of,
        state,
        word,
        tone,
        why: why || null,
        when: schedule && a.cron ? frequencyWord(a.cron) : null,
    };
}

/** Is it running (or on)? */
export const standingLive = (s) => !!s?.known && (s.state === 'running' || s.state === 'on');
/** Off: its card dims. Blocked and Failing stay bright, they want a look. */
export const standingOff = (s) => !!s?.known && s.state === 'off';

/** The space's automations by id, read only while some contract is an automation's. */
export function useStandingAutomations(groupId, enabled) {
    const res = useLoad(async () => {
        const r = await autosApi.list(groupId);
        return (r?.automations || []).filter((x) => x?.id).map(automationItem);
    }, [groupId], { enabled });
    const names = usePluginNames(enabled);
    return useMemo(() => ({
        byId: new Map((res.data || []).map((a) => [String(a.id), a])),
        loaded: !!res.data,
        error: res.error || null,
        names,
    }), [res.data, res.error, names]);
}

/** The space's contracts, each automation grant named and stood as its automation:
 *  `label` is the schedule's "‹Plugin› · ‹Tool›" or the automation's name, and
 *  `standing` says how it stands. Every other contract is as read. */
export function withStanding(profiles, autos) {
    return profiles.map((p) => {
        if (!isStanding(p)) return p;
        const a = autos.byId.get(String(p.entityId)) || null;
        const s = standingOf(p, a, autos.names);
        // read, and not there: the automation was deleted
        if (!a && autos.loaded) s.gone = true;
        return { ...p, label: s.name, standing: s };
    });
}
